import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const EditAnimalDialog = ({ 
  open, 
  onClose, 
  animal, 
  onUpdateComplete 
}) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (animal && open) {
      setForm({
        name: animal.name || '',
        species: animal.species || 'Dog',
        breed: animal.breed || '',
        gender: animal.gender || '',
        color: animal.color || '',
        date_of_birth: animal.date_of_birth || '',
        microchip_number: animal.microchip_number || '',
        health_status: animal.health_status || 'healthy',
        temperament: animal.temperament || '',
        special_needs: animal.special_needs || '',
        adoption_fee: animal.adoption_fee || '',
        status: animal.status || 'available'
      });
    }
  }, [animal, open]);

  const handleSubmit = async () => {
    if (!animal) return;
    
    setLoading(true);
    try {
      await axios.put(`/shelter/animals/${animal.animal_id}`, form);
      toast.success(`${form.name} updated successfully!`);
      onUpdateComplete();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update animal');
    } finally {
      setLoading(false);
    }
  };

  if (!animal) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Animal - {animal.name}</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 py-4">
          <div>
            <Label>Name *</Label>
            <Input
              value={form.name}
              onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>Species</Label>
            <Select 
              value={form.species} 
              onValueChange={value => setForm(prev => ({ ...prev, species: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Dog">Dog</SelectItem>
                <SelectItem value="Cat">Cat</SelectItem>
                <SelectItem value="Bird">Bird</SelectItem>
                <SelectItem value="Rabbit">Rabbit</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Breed</Label>
            <Input
              value={form.breed}
              onChange={e => setForm(prev => ({ ...prev, breed: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>Gender</Label>
            <Select 
              value={form.gender} 
              onValueChange={value => setForm(prev => ({ ...prev, gender: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Color</Label>
            <Input
              value={form.color}
              onChange={e => setForm(prev => ({ ...prev, color: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>Date of Birth</Label>
            <Input
              type="date"
              value={form.date_of_birth}
              onChange={e => setForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>Microchip Number</Label>
            <Input
              value={form.microchip_number}
              onChange={e => setForm(prev => ({ ...prev, microchip_number: e.target.value }))}
            />
          </div>
          
          <div>
            <Label>Health Status</Label>
            <Select 
              value={form.health_status} 
              onValueChange={value => setForm(prev => ({ ...prev, health_status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="healthy">Healthy</SelectItem>
                <SelectItem value="under_treatment">Under Treatment</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="recovering">Recovering</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Status</Label>
            <Select 
              value={form.status} 
              onValueChange={value => setForm(prev => ({ ...prev, status: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="fostered">Fostered</SelectItem>
                <SelectItem value="medical_hold">Medical Hold</SelectItem>
                <SelectItem value="quarantine">Quarantine</SelectItem>
                <SelectItem value="pending_adoption">Pending Adoption</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Adoption Fee ($)</Label>
            <Input
              type="number"
              value={form.adoption_fee}
              onChange={e => setForm(prev => ({ ...prev, adoption_fee: e.target.value }))}
            />
          </div>
          
          <div className="col-span-2">
            <Label>Temperament</Label>
            <Input
              value={form.temperament}
              onChange={e => setForm(prev => ({ ...prev, temperament: e.target.value }))}
              placeholder="e.g., Friendly, Shy, Energetic"
            />
          </div>
          
          <div className="col-span-2">
            <Label>Special Needs</Label>
            <Textarea
              value={form.special_needs}
              onChange={e => setForm(prev => ({ ...prev, special_needs: e.target.value }))}
              placeholder="Any special requirements or medical conditions..."
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditAnimalDialog;
